/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * ContactDetailPage -- R13 web Round 1.
 *
 * Tabs:
 *   - Overview  (read + edit: full_name, organisation, role, phone,
 *                alt_phones[], email, alt_emails[], address, discussed,
 *                next_action)
 *   - Facts     (grouped by kind; shareable toggle)
 *   - Focus     (industry, intent, challenge, refine_note)
 *   - Feedback  (per-contact history)
 *
 * All APIs are shared with mobile (MOBILE_API.md).
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import connectApi from '../../services/connectApi';

const SKD_CYAN_DARK = '#0e7490';

const TABS = ['overview', 'facts', 'focus', 'feedback'];

export default function ContactDetailPage() {
  const { id } = useParams();
  const [tab, setTab] = useState('overview');
  const [contact, setContact] = useState(null);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    try { setContact(await connectApi.getContact(id)); setErr(null); }
    catch (e) { setErr(e.message || 'Could not load contact.'); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (err) return <div style={styles.errorBox}>{err} <Link to="/connect/contacts" style={styles.link}>Back to list</Link></div>;
  if (!contact) return <div style={styles.muted}>Loading…</div>;

  const latest = contact.latest_pitch || null;
  const prepareLabel = latest && latest.status === 'draft'
    ? 'Open invite draft'
    : (latest && latest.status && latest.status !== 'draft'
        ? 'Prepare a new invite'
        : 'Prepare invite');

  return (
    <section>
      <div style={{ marginBottom: 10 }}>
        <Link to="/connect/contacts" style={styles.link}>&larr; All contacts</Link>
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <h1 style={styles.h1}>{contact.full_name || <em style={{ color: '#94a3b8' }}>(no name)</em>}</h1>
          <div style={{ color: '#64748b', fontSize: 14 }}>
            {[contact.organisation, contact.role].filter(Boolean).join(' · ') || <span style={{ color: '#94a3b8' }}>Add organisation / role in Overview.</span>}
          </div>
        </div>
        <Link to={`/connect/contacts/${encodeURIComponent(contact.id || contact._id)}/invite`}
              style={styles.prepareBtn}>
          {prepareLabel}
        </Link>
      </div>

      <div style={styles.tabBar}>
        {TABS.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            style={{ ...styles.tab, ...(tab === t ? styles.tabActive : {}) }}
          >
            {t === 'feedback' ? 'Feedback history' : t[0].toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {tab === 'overview' && <OverviewTab contact={contact} onSaved={load} />}
      {tab === 'facts'    && <FactsTab contactId={contact.id || contact._id} />}
      {tab === 'focus'    && <FocusTab contact={contact} onSaved={load} />}
      {tab === 'feedback' && <FeedbackHistoryTab contactId={contact.id || contact._id} />}
    </section>
  );
}

// ─── Overview tab (edit in place) ──────────────────────────────────────────
function OverviewTab({ contact, onSaved }) {
  const [draft, setDraft] = useState({});
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);

  const altPhonesStr = (contact.alt_phones || []).join(', ');
  const altEmailsStr = (contact.alt_emails || []).join(', ');

  useEffect(() => {
    setDraft({
      full_name: contact.full_name || '',
      organisation: contact.organisation || '',
      role: contact.role || '',
      phone: contact.phone || '',
      alt_phones_text: altPhonesStr,
      email: contact.email || '',
      alt_emails_text: altEmailsStr,
      address: contact.address || '',
      discussed: contact.discussed || '',
      next_action: contact.next_action || ''
    });
    setNote(null); setErr(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contact.id, contact._id]);

  const save = async () => {
    setBusy(true); setErr(null); setNote(null);
    try {
      const altPhones = draft.alt_phones_text.split(/[,\n]/).map((s) => s.trim()).filter(Boolean);
      const altEmails = draft.alt_emails_text.split(/[,\n]/).map((s) => s.trim().toLowerCase()).filter(Boolean);
      await connectApi.patchContact(contact.id || contact._id, {
        full_name: draft.full_name,
        organisation: draft.organisation,
        role: draft.role,
        phone: draft.phone || null,
        alt_phones: altPhones,
        email: draft.email || null,
        alt_emails: altEmails,
        address: draft.address,
        discussed: draft.discussed,
        next_action: draft.next_action
      });
      setNote('Saved.');
      await onSaved();
    } catch (e) {
      setErr(e.message || 'Save failed.');
    } finally { setBusy(false); }
  };

  const field = (key, label, extra = {}) => (
    <label style={styles.field}>
      <span style={styles.fieldLabel}>{label}</span>
      <input
        value={draft[key] || ''}
        onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
        style={styles.input}
        {...extra}
      />
    </label>
  );
  const textarea = (key, label, rows = 3) => (
    <label style={styles.field}>
      <span style={styles.fieldLabel}>{label}</span>
      <textarea
        value={draft[key] || ''}
        onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
        rows={rows}
        style={{ ...styles.input, resize: 'vertical', fontFamily: 'inherit' }}
      />
    </label>
  );

  return (
    <div style={styles.card}>
      <div style={styles.grid2}>
        {field('full_name', 'Full name')}
        {field('organisation', 'Organisation')}
        {field('role', 'Role / title')}
        {field('address', 'Address')}
        {field('phone', 'Phone (primary)', { placeholder: '+91 98765 43210' })}
        {field('email', 'Email (primary)', { type: 'email' })}
        {field('alt_phones_text', 'Alt phones (comma-separated)')}
        {field('alt_emails_text', 'Alt emails (comma-separated)')}
      </div>
      {textarea('discussed', 'What was discussed', 3)}
      {textarea('next_action', 'Next action', 2)}

      {err  && <div style={styles.errorBox}>{err}</div>}
      {note && <div style={styles.okBox}>{note}</div>}

      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button type="button" style={styles.btnPrimary} disabled={busy} onClick={save}>
          {busy ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

// ─── Facts tab ────────────────────────────────────────────────────────────
function FactsTab({ contactId }) {
  const [facts, setFacts] = useState(null);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    try { setFacts(await connectApi.listFacts(contactId)); setErr(null); }
    catch (e) { setErr(e.message || 'Could not load facts.'); setFacts([]); }
  }, [contactId]);
  useEffect(() => { load(); }, [load]);

  const toggleShareable = async (fact) => {
    try {
      await connectApi.patchFact(fact.id || fact._id, { shareable: !fact.shareable });
      await load();
    } catch (e) {
      setErr(e.message || 'Could not update.');
    }
  };

  if (facts === null) return <div style={styles.muted}>Loading…</div>;
  if (!facts.length) {
    return (
      <div style={styles.emptyCard}>
        What we know about this contact is empty. Facts land from brochure
        scans, QR codes, and voice-note summaries (mobile today, web in
        Round 3).
      </div>
    );
  }

  const groups = _groupFacts(facts);

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      {err && <div style={styles.errorBox}>{err}</div>}
      {Object.keys(groups).map((kind) => (
        <div key={kind} style={styles.card}>
          <h3 style={{ margin: '0 0 10px', color: '#0f172a' }}>{_prettyKind(kind)}</h3>
          <div style={{ display: 'grid', gap: 8 }}>
            {groups[kind].map((f) => (
              <div key={f.id || f._id} style={styles.factRow}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: '#0f172a', fontSize: 14 }}>{_factPrimary(f)}</div>
                  {_factSecondary(f) && (
                    <div style={{ color: '#64748b', fontSize: 12, marginTop: 2 }}>{_factSecondary(f)}</div>
                  )}
                  <div style={{ color: '#94a3b8', fontSize: 11, marginTop: 2 }}>
                    source: {f.source}{f.confidence != null ? ` · confidence ${Math.round(f.confidence * 100)}%` : ''}
                  </div>
                </div>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#64748b', whiteSpace: 'nowrap' }}>
                  <input type="checkbox" checked={!!f.shareable} onChange={() => toggleShareable(f)} />
                  shareable
                </label>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function _groupFacts(facts) {
  const order = ['product', 'service', 'certification', 'company_profile', 'mission', 'address', 'location', 'need', 'qr_payload', 'note', 'location_insight', 'other'];
  const out = {};
  for (const k of order) out[k] = [];
  for (const f of facts) {
    const kind = out[f.kind] ? f.kind : 'other';
    out[kind].push(f);
  }
  for (const k of Object.keys(out)) if (!out[k].length) delete out[k];
  return out;
}
function _prettyKind(k) {
  const m = {
    product: 'Products', service: 'Services', certification: 'Certifications',
    company_profile: 'Company profile', mission: 'Mission', address: 'Addresses',
    location: 'Locations', need: 'Needs', qr_payload: 'QR payloads',
    note: 'Notes', location_insight: 'Location insights', other: 'Other'
  };
  return m[k] || k;
}
function _factPrimary(f) {
  const v = f.value || {};
  if (v.name) return v.name;
  if (v.title) return v.title;
  if (v.text) return v.text;
  if (v.line) return v.line;
  if (v.value) return typeof v.value === 'string' ? v.value : JSON.stringify(v.value);
  if (v.label) return v.label;
  return JSON.stringify(v).slice(0, 120);
}
function _factSecondary(f) {
  const v = f.value || {};
  if (v.description) return v.description;
  if (v.summary) return v.summary;
  if (v.detail) return v.detail;
  if (v.org) return v.org;
  return null;
}

// ─── Focus tab ─────────────────────────────────────────────────────────────
function FocusTab({ contact, onSaved }) {
  const [draft, setDraft] = useState({});
  const [err, setErr] = useState(null);
  const [note, setNote] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setDraft({
      industry:    contact.industry    || '',
      intent:      contact.intent      || '',
      challenge:   contact.challenge   || '',
      refine_note: contact.refine_note || ''
    });
    setNote(null); setErr(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contact.id, contact._id]);

  const INTENTS = [
    { v: '',                        label: 'Not set' },
    { v: 'upskill_team',            label: 'Upskill team' },
    { v: 'ai_adoption',             label: 'AI adoption' },
    { v: 'digital_transformation',  label: 'Digital transformation' },
    { v: 'business_growth',         label: 'Business growth' },
    { v: 'pilot_project',           label: 'Pilot project' },
    { v: 'faculty_upskilling',      label: 'Faculty upskilling (university / institute)' },
    { v: 'industry_ready_curriculum', label: 'Industry-ready curriculum' },
    { v: 'hiring_placement',        label: 'Hiring / placement' },
    { v: 'partnership',             label: 'Partnership' }
  ];

  const save = async () => {
    setBusy(true); setErr(null); setNote(null);
    try {
      await connectApi.patchContact(contact.id || contact._id, {
        industry:    draft.industry    || null,
        intent:      draft.intent      || null,
        challenge:   draft.challenge   || null,
        refine_note: draft.refine_note || null
      });
      setNote('Saved. The invite drafter will pick up these controls on the next regenerate.');
      await onSaved();
    } catch (e) { setErr(e.message || 'Save failed.'); }
    finally { setBusy(false); }
  };

  return (
    <div style={styles.card}>
      <p style={{ color: '#64748b', fontSize: 14, marginTop: 0 }}>
        Focus steers the invite drafter and the welcome page. Industry is free
        text; the server canonicalises it before matching offerings.
      </p>
      <label style={styles.field}>
        <span style={styles.fieldLabel}>Industry</span>
        <input
          value={draft.industry}
          onChange={(e) => setDraft({ ...draft, industry: e.target.value })}
          placeholder="e.g. Handicrafts & Lifestyle"
          style={styles.input}
          maxLength={120}
        />
      </label>
      <label style={styles.field}>
        <span style={styles.fieldLabel}>Intent</span>
        <select
          value={draft.intent}
          onChange={(e) => setDraft({ ...draft, intent: e.target.value })}
          style={styles.input}
        >
          {INTENTS.map((o) => <option key={o.v || 'unset'} value={o.v}>{o.label}</option>)}
        </select>
      </label>
      <label style={styles.field}>
        <span style={styles.fieldLabel}>Challenge (one line)</span>
        <input
          value={draft.challenge}
          onChange={(e) => setDraft({ ...draft, challenge: e.target.value })}
          placeholder="One sentence on the hard thing to solve"
          style={styles.input}
          maxLength={200}
        />
      </label>
      <label style={styles.field}>
        <span style={styles.fieldLabel}>Refine note (optional)</span>
        <textarea
          value={draft.refine_note}
          onChange={(e) => setDraft({ ...draft, refine_note: e.target.value })}
          rows={2}
          placeholder="Extra context for the drafter -- never quoted on the invite."
          style={{ ...styles.input, resize: 'vertical', fontFamily: 'inherit' }}
          maxLength={600}
        />
      </label>
      {err  && <div style={styles.errorBox}>{err}</div>}
      {note && <div style={styles.okBox}>{note}</div>}
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button type="button" style={styles.btnPrimary} disabled={busy} onClick={save}>
          {busy ? 'Saving…' : 'Save Focus'}
        </button>
      </div>
    </div>
  );
}

// ─── Feedback history tab ─────────────────────────────────────────────────
function FeedbackHistoryTab({ contactId }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try { setRows(await connectApi.listFeedbackForContact(contactId)); }
      catch (e) { setErr(e.message || 'Could not load feedback.'); setRows([]); }
    })();
  }, [contactId]);

  if (rows === null) return <div style={styles.muted}>Loading…</div>;
  if (err) return <div style={styles.errorBox}>{err}</div>;
  if (!rows.length) return <div style={styles.emptyCard}>No feedback from or logged against this contact.</div>;

  return (
    <div style={{ display: 'grid', gap: 10 }}>
      {rows.map((r) => (
        <Link key={r.id} to={`/connect/feedback/${encodeURIComponent(r.id)}`} style={styles.feedbackRow}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <strong style={{ color: '#0f172a' }}>{_prettyType(r.type)}</strong>
            <span style={{ fontSize: 12, color: '#94a3b8' }}>{new Date(r.created_at).toLocaleString()}</span>
          </div>
          <div style={{ color: '#64748b', fontSize: 13, marginTop: 4 }}>
            via {r.source} · status {r.status}
            {r.step_title ? ` · ${r.step_title}` : ''}
          </div>
          {r.text_preview && (
            <div style={{ color: '#334155', fontSize: 13, marginTop: 6 }}>{r.text_preview}</div>
          )}
        </Link>
      ))}
    </div>
  );
}

function _prettyType(t) {
  return (t || '').replace(/_/g, ' ');
}

const styles = {
  h1: { fontSize: 24, color: '#0f172a', margin: '4px 0 2px' },
  link: { color: SKD_CYAN_DARK, textDecoration: 'none', fontSize: 13 },
  tabBar: {
    display: 'flex', gap: 2, marginTop: 16, marginBottom: 14,
    borderBottom: '1px solid #e2e8f0'
  },
  tab: {
    padding: '8px 14px', background: 'transparent', border: 'none',
    borderBottom: '2px solid transparent',
    color: '#64748b', fontSize: 14, cursor: 'pointer'
  },
  tabActive: {
    color: '#0f172a', borderBottom: `2px solid ${SKD_CYAN_DARK}`, fontWeight: 600
  },
  card: {
    background: 'white', padding: 16, borderRadius: 10, border: '1px solid #e2e8f0'
  },
  grid2: {
    display: 'grid', gap: 12,
    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))'
  },
  field: {
    display: 'block', marginBottom: 10
  },
  fieldLabel: {
    display: 'block', fontSize: 12, color: '#64748b', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.03em'
  },
  input: {
    width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1',
    fontSize: 14, fontFamily: 'inherit', background: 'white', boxSizing: 'border-box'
  },
  btnPrimary: {
    padding: '8px 16px', borderRadius: 8, background: '#0891b2', color: 'white',
    border: 'none', fontSize: 14, fontWeight: 600, cursor: 'pointer'
  },
  errorBox: {
    padding: 10, background: '#fef2f2', color: '#991b1b',
    borderRadius: 8, border: '1px solid #fecaca', marginTop: 10, fontSize: 13
  },
  okBox: {
    padding: 10, background: '#f0fdf4', color: '#166534',
    borderRadius: 8, border: '1px solid #bbf7d0', marginTop: 10, fontSize: 13
  },
  emptyCard: {
    padding: 16, background: 'white', border: '1px dashed #cbd5e1', borderRadius: 10,
    color: '#64748b', fontSize: 14
  },
  muted: { color: '#94a3b8', padding: 20, textAlign: 'center' },
  factRow: {
    display: 'flex', alignItems: 'flex-start', gap: 12,
    padding: '10px 12px', borderRadius: 8,
    background: '#f8fafc', border: '1px solid #e2e8f0'
  },
  feedbackRow: {
    display: 'block', padding: 12, borderRadius: 10,
    background: 'white', border: '1px solid #e2e8f0',
    textDecoration: 'none', color: '#0f172a'
  },
  prepareBtn: {
    display: 'inline-block', padding: '8px 14px', borderRadius: 8,
    background: '#0891b2', color: 'white', textDecoration: 'none',
    fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap'
  }
};
